import { useEffect, useState } from "react";
import { useTripsCtx } from "../context/TripDetailsContext";
import { useParams } from "react-router-dom";
import { ModeSelection, TTrip } from "../types/TripTypes";
import { updateWandaTripCreationDetails } from "../services/TripService";
import { notification } from "antd";
import { usePersonSummaryCtx } from "../context/PersonSummaryContext";
import moment from "moment";

interface IParams {
  serviceUserId: string;
  ride: string;
  levelOfService: string;
}

export const useTripSummary = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { serviceUserId, ride, levelOfService } = useParams<IParams>();

  const { caregiverSummaryInfo } = usePersonSummaryCtx();

  const {
    tripSetupDetailsCtx,
    tripScheduleDateCtx,
    isApplyButtonClickCtx,
  } = useTripsCtx();
  const [tripScheduleDate] = tripScheduleDateCtx;
  const [tripCreationDetails, setTripCreationDetails] = tripSetupDetailsCtx;
  const [isApplyButtonClick, setIsApplyButtonClick] = isApplyButtonClickCtx;

  const levelOfServiceID: { [key: string]: number } = {
    "Curb-to-Curb": 1,
    "Door-to-Door": 2,
    "Person-to-Person": 3,
  };

  const updateTripDetails = async (
    isTripInitiated: boolean,
    promoCode?: string,
    requestAssignmentId?: number,
    paymentInstrumentId?: number
  ) => {
    try {
      if (isApplyButtonClick) {
        setIsLoading(true);
      }
      const tripLegAddresses = tripCreationDetails.map((leg: TTrip) => {
        const { pickupAddressDefault, dropOffAddressDefault } = leg;
        return {
          pickupAddress: {
            address1: pickupAddressDefault?.address1,
            address2: pickupAddressDefault?.address2,
            city: pickupAddressDefault?.city,
            county: pickupAddressDefault?.county,
            state: pickupAddressDefault?.state,
            zipCode: pickupAddressDefault?.zip,
            latitude: pickupAddressDefault?.latitude,
            longitude: pickupAddressDefault?.longitude,
          },
          dropOffAddress: {
            address1: dropOffAddressDefault?.address1,
            address2: dropOffAddressDefault?.address2,
            city: dropOffAddressDefault?.city,
            county: dropOffAddressDefault?.county,
            state: dropOffAddressDefault?.state,
            zipCode: dropOffAddressDefault?.zip,
            latitude: dropOffAddressDefault?.latitude,
            longitude: dropOffAddressDefault?.longitude,
          },
        };
      });

      const tripCreationPayload = {
        personId: Number(serviceUserId),
        careGiverPersonId: caregiverSummaryInfo.personId,
        tripDate: moment(tripScheduleDate).format("YYYY-MM-DD"),
        requestAssignmentId,
        paymentInstrumentId,
        levelOfService: levelOfServiceID[levelOfService],
        vehicleType:
          ride === "Standard" ? ModeSelection.Standard : ModeSelection.WAV,
        isTripInitiated,
        addressRequests: tripLegAddresses,
        promoCode,
      };

      const {
        wandaTripLegDetails,
        ...rest
      } = await updateWandaTripCreationDetails(
        tripCreationPayload,
        Number(caregiverSummaryInfo.personId)
      );

      setTripCreationDetails((tripCreationDetails: TTrip[]) =>
        tripCreationDetails.map((legCreationDetails: TTrip, index: number) => ({
          ...legCreationDetails,
          legCostDetails: wandaTripLegDetails[index],
          ...rest,
        }))
      );
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch Trip Summary.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    updateTripDetails(false);
  }, []);

  return {
    isLoading,
    updateTripDetails,
  };
};
